import { flushSync } from 'react-dom';

export type ThemeMode = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'vectria_theme';
export const THEME_TRANSITION_DURATION_MS = 650;
export const THEME_TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export interface ViewTransitionLike {
  ready: Promise<void>;
  finished: Promise<void>;
  updateCallbackDone: Promise<void>;
  skipTransition?: () => void;
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (updateCallback: () => Promise<void> | void) => ViewTransitionLike;
};

export interface ThemeTransitionOriginEvent {
  currentTarget?: EventTarget | null;
  target?: EventTarget | null;
  clientX?: number;
  clientY?: number;
}

export interface RunCircularThemeTransitionOptions {
  nextTheme: ThemeMode;
  origin?: HTMLElement | EventTarget | null;
  event?: ThemeTransitionOriginEvent | null;
  applyState?: (nextTheme: ThemeMode) => void;
  persist?: boolean;
}

let activeViewTransition: ViewTransitionLike | null = null;
let activeRevealAnimation: Animation | null = null;
let isUpdatingThemeDom = false;

/**
 * Checks whether the user has explicitly persisted a theme preference in localStorage.
 */
export function getPersistedTheme(): ThemeMode | null {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Detects the system theme preference when available, defaulting to 'dark' to preserve
 * Vectria's baseline aesthetic when system preferences are unavailable or dark.
 */
export function getSystemThemePreference(): ThemeMode {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const lightQuery = window.matchMedia('(prefers-color-scheme: light)');
      if (lightQuery && lightQuery.matches) {
        return 'light';
      }
    }
  } catch {
    // Ignore matchMedia errors in restricted or headless environments
  }
  return 'dark';
}

/**
 * Resolves the initial theme from persisted storage or system preference.
 */
export function getInitialTheme(): ThemeMode {
  return getPersistedTheme() ?? getSystemThemePreference();
}

/**
 * Synchronously applies the theme attributes and classes to the DOM and persists if requested.
 */
export function applyThemeToDom(theme: ThemeMode, options?: { persist?: boolean }): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;

  if (document.body) {
    if (theme === 'light') {
      document.body.classList.add('light-theme');
    } else {
      document.body.classList.remove('light-theme');
    }
  }

  if (options?.persist !== false) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage quota or privacy mode errors
    }
  }
}

/**
 * Checks whether reduced motion is enabled by the user's operating system / browser.
 */
export function prefersReducedMotion(): boolean {
  try {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
    }
  } catch {
    // Ignore errors in unsupported environments
  }
  return false;
}

function isElementWithBoundingRect(candidate: unknown): candidate is HTMLElement {
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    'getBoundingClientRect' in candidate &&
    typeof (candidate as HTMLElement).getBoundingClientRect === 'function'
  );
}

/**
 * Resolves the center coordinates of the clicked/activated theme toggle button
 * using getBoundingClientRect(). Works for both pointer clicks and keyboard activation.
 */
export function resolveToggleCenter(
  origin?: HTMLElement | EventTarget | null,
  event?: ThemeTransitionOriginEvent | null
): { x: number; y: number } {
  const viewportWidth = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1280;
  const viewportHeight = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 720;

  let targetElement: HTMLElement | null = null;

  if (isElementWithBoundingRect(origin)) {
    targetElement = origin;
  } else if (isElementWithBoundingRect(event?.currentTarget)) {
    targetElement = event.currentTarget;
  } else if (isElementWithBoundingRect(event?.target)) {
    const closestButton =
      typeof (event.target as HTMLElement).closest === 'function'
        ? (event.target as HTMLElement).closest('button')
        : null;
    targetElement = isElementWithBoundingRect(closestButton) ? closestButton : event.target;
  } else if (typeof document !== 'undefined' && isElementWithBoundingRect(document.activeElement)) {
    targetElement = document.activeElement;
  }

  if (targetElement) {
    const rect = targetElement.getBoundingClientRect();
    const hasBoxGeometry =
      rect.width > 0 || rect.height > 0 || rect.left !== 0 || rect.top !== 0;

    if (hasBoxGeometry) {
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    }

    if (
      typeof event?.clientX === 'number' &&
      typeof event?.clientY === 'number' &&
      (event.clientX !== 0 || event.clientY !== 0)
    ) {
      return {
        x: event.clientX,
        y: event.clientY,
      };
    }

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  if (
    typeof event?.clientX === 'number' &&
    typeof event?.clientY === 'number' &&
    (event.clientX !== 0 || event.clientY !== 0)
  ) {
    return {
      x: event.clientX,
      y: event.clientY,
    };
  }

  return {
    x: viewportWidth / 2,
    y: viewportHeight / 2,
  };
}

/**
 * Calculates the maximum Euclidean radius required for a circle centered at (x, y)
 * to cover the entire viewport, including all four corners.
 */
export function calculateMaxRevealRadius(
  x: number,
  y: number,
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0
): number {
  const safeWidth = Math.max(viewportWidth, 0);
  const safeHeight = Math.max(viewportHeight, 0);
  const maxHorizontalDistance = Math.max(x, safeWidth - x);
  const maxVerticalDistance = Math.max(y, safeHeight - y);
  return Math.hypot(maxHorizontalDistance, maxVerticalDistance);
}

function cleanupTransitionVariables(root: HTMLElement): void {
  root.removeAttribute('data-theme-transition');
  root.style.removeProperty('--theme-reveal-x');
  root.style.removeProperty('--theme-reveal-y');
  root.style.removeProperty('--theme-reveal-end-radius');
}

/**
 * Executes a Circular Reveal theme transition using the native View Transitions API
 * and CSS clip-path: circle(). Falls back to an immediate theme switch when
 * prefers-reduced-motion is enabled or View Transitions are unsupported.
 */
export function runCircularThemeTransition({
  nextTheme,
  origin,
  event,
  applyState,
  persist = true,
}: RunCircularThemeTransitionOptions): ViewTransitionLike | null {
  if (typeof document === 'undefined') {
    applyState?.(nextTheme);
    return null;
  }

  // Prevent re-entrant synchronous conflicts while a DOM update callback is executing
  if (isUpdatingThemeDom) {
    return activeViewTransition;
  }

  // Handle rapid repeated clicks by cleanly aborting any in-flight transition first
  if (activeRevealAnimation) {
    try {
      activeRevealAnimation.cancel();
    } catch {
      // Ignore animation cancel errors
    }
    activeRevealAnimation = null;
  }

  if (activeViewTransition) {
    const previousTransition = activeViewTransition;
    activeViewTransition = null;
    try {
      previousTransition.skipTransition?.();
    } catch {
      // Ignore skipTransition errors on already-settled transitions
    }
  }

  const doc = document as DocumentWithViewTransition;
  const root = document.documentElement;
  const canUseViewTransition =
    typeof doc.startViewTransition === 'function' && !prefersReducedMotion();

  const commitThemeUpdate = () => {
    isUpdatingThemeDom = true;
    try {
      if (applyState) {
        flushSync(() => {
          applyState(nextTheme);
        });
      }
      applyThemeToDom(nextTheme, { persist });
      // Force synchronous style recalculation so the incoming View Transition snapshot
      // captures the exact final theme colors with zero intermediate transition frames.
      void root.offsetHeight;
    } finally {
      isUpdatingThemeDom = false;
    }
  };

  if (!canUseViewTransition) {
    cleanupTransitionVariables(root);
    commitThemeUpdate();
    return null;
  }

  const { x, y } = resolveToggleCenter(origin, event);
  const maxRadius = calculateMaxRevealRadius(x, y);

  root.style.setProperty('--theme-reveal-x', `${x}px`);
  root.style.setProperty('--theme-reveal-y', `${y}px`);
  root.style.setProperty('--theme-reveal-end-radius', `${maxRadius}px`);
  root.setAttribute('data-theme-transition', 'active');

  let transition: ViewTransitionLike;
  try {
    transition = doc.startViewTransition!(() => {
      commitThemeUpdate();
    });
  } catch {
    cleanupTransitionVariables(root);
    commitThemeUpdate();
    return null;
  }

  activeViewTransition = transition;

  Promise.resolve(transition.ready)
    .then(() => {
      if (activeViewTransition !== transition) return;
      if (typeof root.animate === 'function') {
        activeRevealAnimation = root.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${maxRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: THEME_TRANSITION_DURATION_MS,
            easing: THEME_TRANSITION_EASING,
            fill: 'forwards',
            pseudoElement: '::view-transition-new(root)',
          }
        );
      }
    })
    .catch(() => {
      // Transition may be skipped or aborted when rapidly clicking; safe to ignore.
    });

  Promise.resolve(transition.finished)
    .catch(() => {
      // Ignore rejected finished promises from skipped transitions
    })
    .finally(() => {
      if (activeViewTransition === transition) {
        activeViewTransition = null;
        activeRevealAnimation = null;
        cleanupTransitionVariables(root);
      }
    });

  return transition;
}

